// Product-spec 16-stage vocabulary for MSME loan opportunity lifecycle.
// Used across borrower / lender / RM dashboards and applications tables so
// every screen speaks the same language and avoids generic "Approved" labels.
export const STAGES = [
  { key: "passport_incomplete",       label: "Passport incomplete",             tone: "slate"  },
  { key: "passport_ready",            label: "Passport ready",                  tone: "slate"  },
  { key: "opportunity_identified",    label: "Opportunity identified",          tone: "sky"    },
  { key: "consent_pending",           label: "Consent pending",                 tone: "amber"  },
  { key: "shared_with_lender",        label: "Shared with lender",              tone: "sky"    },
  { key: "under_lender_review",       label: "Under lender review",             tone: "sky"    },
  { key: "lender_interested",         label: "Lender interested",               tone: "orange" },
  { key: "rm_assigned",               label: "RM assigned",                     tone: "orange" },
  { key: "borrower_contacted",        label: "Borrower contacted",              tone: "orange" },
  { key: "additional_info_requested", label: "Additional information requested",tone: "amber"  },
  { key: "formal_application",        label: "Formal application initiated",    tone: "emerald"},
  { key: "under_appraisal",           label: "Under appraisal",                 tone: "emerald"},
  { key: "sanctioned",                label: "Sanctioned by lender",            tone: "emerald"},
  { key: "declined",                  label: "Declined by lender",              tone: "red"    },
  { key: "withdrawn",                 label: "Withdrawn by MSME",               tone: "slate"  },
  { key: "disbursed",                 label: "Disbursed by lender",             tone: "emerald"},
  { key: "partially_disbursed",       label: "Partially disbursed",             tone: "emerald"},
  { key: "closed",                    label: "Closed",                          tone: "slate"  },
];

const BY_KEY = Object.fromEntries(STAGES.map((s) => [s.key, s]));
const BY_LABEL = Object.fromEntries(STAGES.map((s) => [s.label.toLowerCase(), s]));

// Legacy → new mapping so old status values coming back from the API map onto
// the new vocabulary without a data migration.
const LEGACY_MAP = {
  "draft":                 "passport_incomplete",
  "profile incomplete":    "passport_incomplete",
  "ready for matching":    "passport_ready",
  "discoverable":          "opportunity_identified",
  "lender interest received": "lender_interested",
  "borrower consent pending": "consent_pending",
  "application shared":    "shared_with_lender",
  "assigned to branch":    "rm_assigned",
  "initial review":        "under_lender_review",
  "additional documents required": "additional_info_requested",
  "field verification":    "under_appraisal",
  "credit appraisal":      "under_appraisal",
  "credit decision pending": "under_appraisal",
  "indicatively approved": "under_appraisal",
  "sanction accepted":     "sanctioned",
  "documentation pending": "sanctioned",
  "disbursement pending":  "sanctioned",
  "rejected":              "declined",
  "expired":               "closed",
  "eligible":            "opportunity_identified",
  "approved":            "sanctioned",
  "sanction":            "sanctioned",
  "sanctioned":          "sanctioned",
  "in progress":         "under_appraisal",
  "under review":        "under_lender_review",
  "interested":          "lender_interested",
  "expressed interest":  "lender_interested",
  "application submitted": "formal_application",
  "application initiated": "formal_application",
  "consent granted":     "shared_with_lender",
  "pending consent":     "consent_pending",
  "declined":            "declined",
  "disbursed":           "disbursed",
  "partially disbursed": "partially_disbursed",
  "closed":              "closed",
  "withdrawn":           "withdrawn",
};

export function normaliseStatus(raw) {
  if (!raw) return BY_KEY.passport_incomplete;
  const key = String(raw).toLowerCase().trim();
  const mapped = LEGACY_MAP[key] || key;
  return BY_KEY[mapped] || BY_LABEL[key] || { key: "unknown", label: raw, tone: "slate" };
}

const TONE_CLASS = {
  slate:   "bg-slate-100 text-slate-800 border-slate-200",
  sky:     "bg-sky-100 text-sky-900 border-sky-200",
  amber:   "bg-amber-100 text-amber-900 border-amber-200",
  orange:  "bg-orange-100 text-orange-900 border-orange-200",
  emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
  red:     "bg-red-100 text-red-900 border-red-200",
};

export function toneClass(tone) {
  return TONE_CLASS[tone] || TONE_CLASS.slate;
}

const OUTCOME_SANCTIONED = new Set(["sanctioned", "disbursed", "partially_disbursed"]);
const OUTCOME_CLOSED = new Set(["declined", "withdrawn", "closed"]);
export const isSanctionedOutcome = (raw) => OUTCOME_SANCTIONED.has(normaliseStatus(raw).key);
export const isClosedOutcome = (raw) => OUTCOME_CLOSED.has(normaliseStatus(raw).key);
// Raw lender-side statuses that still need a disbursement follow-up.
export const AWAITING_DISBURSEMENT = ["sanctioned", "sanction accepted", "documentation pending", "disbursement pending", "partially disbursed"];
